import React, { useContext, useEffect, useState } from 'react';
import { OrderContext } from '../App';
import { fetchOrderDetails, estTimeCalc, reverseGeocode } from '../services/api';
import { COMPLETED_STATUSES, formatDistanceKm, getOrderStatusLabel } from '../utils/orderDisplay';
import OrderStatusGrid from './OrderStatusGrid';
import OrderTimeline from './OrderTimeline';
import './OrderExtraInfo.css';

function OrderExtraInfo() {
    const { selectedOrder, instance, coordinates, etaInfo, setEtaInfo, setCurrentLocation, selectedClientId } = useContext(OrderContext);
    const [travelData, setTravelData] = useState({
        travelHistory: [],
        eta: '',
        estimatedArrival: '',
        status: '',
        orderId: '',
        rawDetails: []
    });
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
    const [loadingEta, setLoadingEta] = useState(false);

    useEffect(() => {
        if (!selectedOrder) return;

        setLoadingOrderDetails(true);
        setCurrentLocation && setCurrentLocation(null);
        setLoadingEta(true);

        // Fetch order details
        fetchOrderDetails(selectedOrder.order_id, instance, selectedClientId)
            .then(data => {
                const isComplete = COMPLETED_STATUSES.includes(selectedOrder.Status);
                setTravelData(prev => ({
                    ...prev,
                    ...data,
                    // Preserve any previously computed ETA unless we have a new one or the load is complete
                    estimatedArrival: isComplete
                        ? 'Complete'
                        : (data.estimatedArrival || prev.estimatedArrival || '')
                }));
            })
            .catch(error => {
                setTravelData({
                    travelHistory: [],
                    eta: '',
                    status: '', 
                    orderId: selectedOrder.order_id,
                    rawDetails: []
                });
            })
            .finally(() => setLoadingOrderDetails(false));

        // If order is complete, don't calculate ETA; just show complete
        const isComplete = COMPLETED_STATUSES.includes(selectedOrder.Status);
        if (isComplete) {
            setTravelData(prev => ({
                ...prev,
                estimatedArrival: 'Complete'
            }));
            setLoadingEta(false);
            return;
        }

        // Fetch ETA for any pre-complete status (invert: not in COMPLETED_STATUSES)
        if (selectedOrder['Pickup Date'] && !COMPLETED_STATUSES.includes(selectedOrder.Status)) {
            const runEta = async () => {
                try {
                    // Ensure we have coordinates; if missing, geocode addresses
                    let pickup = coordinates.pickup;
                    let delivery = coordinates.delivery;

                    if (!pickup && selectedOrder?.origin_address) {
                        const loc = await reverseGeocode(selectedOrder.origin_address, instance);
                        if (loc) pickup = [loc.lat, loc.lon];
                    }
                    if (!delivery && selectedOrder?.destination_address) {
                        const loc = await reverseGeocode(selectedOrder.destination_address, instance);
                        if (loc) delivery = [loc.lat, loc.lon];
                    }

                    if (!pickup || !delivery) {
                        setLoadingEta(false);
                        return;
                    }

                    const etaData = await estTimeCalc(
                        pickup[0],
                        pickup[1],
                        delivery[0],
                        delivery[1],
                selectedOrder?.VehicleNum || '',
                instance
                    );

                if (etaData) {
                    setEtaInfo(etaData);
                        if (etaData.proximity && setCurrentLocation) {
                            setCurrentLocation(etaData.proximity);
                        }
                        // If we have a vehicle, use "now" as the anchor (Samsara live position),
                        // otherwise fall back to pickup time.
                        let arrivalTime;
                        if (selectedOrder?.VehicleNum) {
                            arrivalTime = new Date(Date.now() + (etaData.duration_min * 60000));
                        } else {
                    const pickupTime = new Date(selectedOrder['Pickup Date']);
                            arrivalTime = new Date(pickupTime.getTime() + (etaData.duration_min * 60000));
                        }
                    const estimatedArrival = formatDateTime(arrivalTime);
                    setTravelData(prev => ({
                        ...prev,
                        estimatedArrival
                    }));
                }
                    // If no etaData, keep prior ETA instead of blanking it
                } catch (error) {
                // Optionally handle ETA error
                } finally {
                    setLoadingEta(false);
                }
            };

            runEta();
        } else {
            setLoadingEta(false);
        }
    }, [selectedOrder, instance]);

    // Helper function to format date and time
    const formatDateTime = (date) => {
        if (!date) return '';
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const distanceText = COMPLETED_STATUSES.includes(selectedOrder?.Status)
        ? '0 km'
        : (loadingEta ? '…' : formatDistanceKm(etaInfo));

    return (
        <div className="order-extra-info">
            <h2>Order Details</h2>
            <OrderStatusGrid
                key={selectedOrder?.order_id}
                className="fade-in"
                orderId={selectedOrder?.order_id || '—'}
                status={getOrderStatusLabel(selectedOrder?.Status)}
                eta={COMPLETED_STATUSES.includes(selectedOrder?.Status)
                    ? 'Complete'
                    : loadingEta
                        ? 'Loading…'
                        : (travelData?.estimatedArrival || '—')}
                distance={distanceText}
                truck={selectedOrder?.VehicleNum || '—'}
                trailer={selectedOrder?.trailer || '—'}
            />

            {loadingOrderDetails ? (
                <div className="loading-spinner fade-in">
                    Loading order details...
                </div>
            ) : (
                <OrderTimeline
                    className="travel-history-container"
                    itemClass="fade-up"
                    events={travelData?.travelHistory}
                    originAddress={selectedOrder?.origin_address}
                    destinationAddress={selectedOrder?.destination_address}
                    shipperTemp={travelData?.shipper_temp ?? selectedOrder?.shipper_temp}
                    consigneeTemp={travelData?.consignee_temp ?? selectedOrder?.consignee_temp}
                    haulTempMin={selectedOrder?.haul_temp_f_min}
                    haulTempMax={selectedOrder?.haul_temp_f_max}
                />
            )}
        </div>
    );
}

export default OrderExtraInfo;